import { Component, effect, inject, OnDestroy, signal, untracked, viewChild } from '@angular/core';
import { FormsModule, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonChip,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonRadio,
  IonRadioGroup,
  IonText,
  IonTitle,
  IonToggle,
  IonToolbar,
  ModalController,
} from '@ionic/angular/standalone';
import { withProductCreate } from '@libs/mtjr/shared/generated/entities/product/product-store-features';

import { NgStyle } from '@angular/common';
import { AttributesModalComponent } from '@apps/mtjr/vendors/app/product/attributes/attributes-sheet-component';
import { AttributesPickerStore } from '@apps/mtjr/vendors/app/product/attributes/attributes.store';
import { VariantsModalComponent } from '@apps/mtjr/vendors/app/product/attributes/variations-sheet.component';
import { VendorStore } from '@apps/mtjr/vendors/app/vendor/list/vendors.page';
import { VxErrorBarComponent } from '@libs/core/lib/error-view/error-bar.component';
import { NgEventBus } from '@libs/core/lib/event-bus/ng-event-bus';
import { VxSubmitButtonComponent } from '@libs/core/lib/form-controls/mtjr-submit-button.component';
import { VxTextAreaFieldComponent, VxTextFieldComponent } from '@libs/core/lib/form-controls/mtjr-text-field.component';
import { errorState } from '@libs/core/lib/state/async-states';
import { Failure, LocalFailure } from '@libs/core/lib/state/failure';
import { AppAlerts } from '@libs/core/lib/utils/app-alerts';
import { VxAttachmentFieldComponent } from '@libs/mtjr/shared/attachment/attachment-field.component';
import { productCreateUtils } from '@libs/mtjr/shared/generated/forms';
import { patchState, signalStore, withMethods } from '@ngrx/signals';
import { TranslatePipe } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

export const ProductCreateStore = signalStore(
  withProductCreate(),
  withMethods((store) => ({
    setAddFailure(failure: Failure) {
      patchState(store, { productCreateState: errorState(failure) });
    },
  })),
);
@Component({
  selector: 'mav-product-create',
  templateUrl: './product-create.page.html',
  providers: [ProductCreateStore],
  imports: [
    IonToggle,
    IonItem,
    IonChip,
    IonLabel,
    IonButton,
    IonText,
    IonTitle,
    IonBackButton,
    IonButtons,
    IonToolbar,
    IonHeader,
    IonContent,
    IonIcon,
    IonRadio,
    IonRadioGroup,
    ReactiveFormsModule,
    FormsModule,
    VxTextFieldComponent,
    VxTextAreaFieldComponent,
    VxSubmitButtonComponent,
    VxErrorBarComponent,
    TranslatePipe,
    VxAttachmentFieldComponent,
    NgStyle,
  ],
})
export class ProductCreatePage implements OnDestroy {
  route = inject(ActivatedRoute);
  store = inject(ProductCreateStore);
  vendorStore = inject(VendorStore);
  attributesStore = new AttributesPickerStore();
  router = inject(Router);
  modalCtr = inject(ModalController);
  createUtils = productCreateUtils(inject(NonNullableFormBuilder));
  advancedMode = signal(false);
  eventBus = inject(NgEventBus);
  imagePickerOpen = signal(false);
  routeSub?: Subscription;
  vendorId: string = this.route.snapshot.params['vendorId'];
  alerts = inject(AppAlerts);
  attachment = viewChild(VxAttachmentFieldComponent);

  formControls = this.createUtils.form.controls;

  constructor() {
    this.formControls.price.addValidators(Validators.required);
    this.formControls.stock.addValidators(Validators.required);

    effect(() => {
      if (this.attributesStore.variants().length > 0) {
        this.formControls.price.removeValidators(Validators.required);
        this.formControls.stock.removeValidators(Validators.required);
      } else {
        this.formControls.price.addValidators(Validators.required);
        this.formControls.stock.addValidators(Validators.required);
      }
      this.formControls.price.updateValueAndValidity();
      this.formControls.stock.updateValueAndValidity();
    });

    effect(() => {
      if (this.store.productCreateState().success) {
        untracked(() => {
          this.router.navigate(['/main/vendors', this.vendorId]);
          this.eventBus.cast('products:fetch');
          this.alerts.successSnackbarTR('PRODUCTS.PRODUCT_CREATED_SUCCESS');
        });
      }
    });

    this.routeSub = this.route.queryParams.subscribe(async (params) => {
      const attribModal = params[AttributesModalComponent.modalId];
      const variantsModal = params[VariantsModalComponent.modalId];
      const activeModal = await this.modalCtr.getTop();
      if (activeModal) return;
      if (attribModal) {
        await this.updateAttributes();
      } else if (variantsModal) {
        await this.showVariants();
      }
    });
  }

  removeImage(url: string) {
    const images = this.createUtils.form.controls.images.value ?? [];
    const index = images.indexOf(url);
    if (index === -1) return;
    const newImages = [...images];
    newImages.splice(index, 1);
    this.createUtils.form.controls.images.setValue(newImages);
  }

  async updateAttributes() {
    const modal = await this.modalCtr.create({
      component: AttributesModalComponent,
      componentProps: {
        attributesStore: this.attributesStore,
      },
      expandToScroll: false,
      focusTrap: true,
      breakpoints: [0, 0.8],
      initialBreakpoint: 0.8,
      cssClass: 'rounded-dialog',
    });
    await modal.present();
  }

  async showVariants() {
    const modal = await this.modalCtr.create({
      component: VariantsModalComponent,
      focusTrap: true,
      breakpoints: [0, 0.8],
      initialBreakpoint: 0.8,
      expandToScroll: false,
      componentProps: {
        attributesStore: this.attributesStore,
      },
      cssClass: 'rounded-dialog',
    });
    await modal.present();
  }

  ngOnDestroy() {
    this.routeSub?.unsubscribe();
  }

  submit() {
    const form = this.createUtils.form;
    const variationsControl = form.controls.variations;
    variationsControl.setValue(this.attributesStore.variants());

    if (form.invalid) {
      form.markAllAsTouched();
      return;
    }

    const result = this.createUtils.parse();
    if (!result.success) {
      this.store.setAddFailure(new LocalFailure());
      return;
    }

    if (this.attachment()?.hasPendingUploads() === true) {
      this.alerts.dangerSnackbarTR('COMMON.ATTACHMENTS_UPLOAD_IN_PROGRESS');
      return;
    }

    this.store.productCreate(result.data, { vendorId: this.vendorId });
  }
}
